/**
 * `/:world/books/:slug`: one work — both titles, the year, the synopsis, where it
 * sits in its sub-series, and where to buy it.
 *
 * Two columns on a wide screen, as on the approved board: the text, and beside
 * it a sidebar — the path nearby (three stops, this one marked), the facts
 * (first edition, place in publication order), and the note about accounts.
 * One column on a narrow screen, the sidebar after the text.
 *
 * Why the book sits where it does is in the text column, under „Защо е шеста
 * спирка“, since it is the reason the page exists.
 *
 * A work in no sub-series says so and links to „Извън поредиците“: a blank
 * series section would read as missing data. A work with no shop listing
 * says it is not on sale at the moment — information, the ordinary state of
 * an out-of-print book, not an error.
 */

import { Link, useParams } from 'react-router'
import { worldPath } from '../../shared/routes.ts'
import type { CatalogueSeries } from '../../shared/catalogue.ts'
import { CataloguePage, usePageTitle, useStops } from '../components/Catalogue.tsx'
import { Knot } from '../components/Ornament.tsx'
import { Path, type PathStop } from '../components/Path.tsx'
import { ReportError } from '../components/ReportError.tsx'
import { Reviews } from '../components/Reviews.tsx'
import { ShelfPanel } from '../components/ShelfPanel.tsx'
import { useI18n } from '../i18n/index.tsx'
import { ordinal } from '../i18n/ordinals.ts'
import {
  editionKey,
  fill,
  mainTitle,
  note,
  otherTitle,
  seriesName,
  synopsis,
  ungroupedLabel,
  workBySlug,
  type Loaded,
} from '../lib/catalogue.ts'

export function Book() {
  return <CataloguePage>{(catalogue, world) => <BookView catalogue={catalogue} world={world} />}</CataloguePage>
}

function BookView({ catalogue, world }: { catalogue: Loaded; world: string }) {
  const { t, locale } = useI18n()
  const { slug } = useParams()
  const work = workBySlug(catalogue, slug)
  usePageTitle(work === undefined ? t('book.not_found.title') : mainTitle(work, locale))

  if (work === undefined) {
    return (
      <section className="panel">
        <h1>{t('book.not_found.title')}</h1>
        <p>
          {t('book.not_found.body')} <Link to={worldPath(world, 'books')}>{t('books.title')}</Link>
        </p>
      </section>
    )
  }

  const other = otherTitle(work, locale)
  const text = synopsis(work, locale)
  const outsideLabel = ungroupedLabel(catalogue.universe, locale)
  const places = work.series.flatMap((membership) => {
    const series = catalogue.series.find((s) => s.slug === membership.slug)
    const index = series === undefined ? -1 : series.books.findIndex((book) => book.work === work.slug)
    return series === undefined || index === -1 ? [] : [{ series, index }]
  })
  const first = places[0]
  const subtitle = [other, work.year_first_published === null ? null : String(work.year_first_published)]
    .filter((part) => part !== null)
    .join(' · ')
  // Its place among the published works, which are in publication order.
  const inPublication = catalogue.works.findIndex((w) => w.slug === work.slug) + 1

  return (
    <div className="book wide">
      <article className="book__text">
        {first === undefined ? null : (
          <p className="book__crumb">
            <Link to={worldPath(world, 'series', first.series.slug)}>{seriesName(first.series, locale)}</Link>
            {' · '}
            {fill(t('book.breadcrumb.stop'), { position: first.index + 1, count: first.series.books.length })}
          </p>
        )}
        <h1>{mainTitle(work, locale)}</h1>
        {subtitle === '' ? null : (
          <p className="book__subtitle" lang={other === null ? undefined : locale === 'bg' ? 'en' : 'bg'}>
            {subtitle}
          </p>
        )}
        <Knot />
        {/* One text node: the drop cap is this paragraph's ::first-letter. */}
        {text === null ? null : <p className="synopsis">{text}</p>}

        {places.map(({ series, index }) => (
          <Why key={series.slug} series={series} index={index} />
        ))}
        {places.length === 0 ? (
          <p className="book__outside">
            {outsideLabel === null ? null : (
              <>
                <Link to={worldPath(world, 'outside')}>{outsideLabel}</Link>.{' '}
              </>
            )}
            {t('book.outside')}
          </p>
        ) : null}

        <h2 className="label">{t('book.buy')}</h2>
        {work.links.length === 0 ? (
          // A state, not data: having no links is what "not on sale" means here.
          <p>{t('book.availability.none')}</p>
        ) : (
          <div className="book__shops">
            {work.links.map((link) => (
              <a key={link.url} className="shop-link" href={link.url} rel="nofollow noopener">
                {t(editionKey(link.medium, link.language))} — {link.retailer}
              </a>
            ))}
          </div>
        )}
        <Reviews world={world} work={work.slug} />
        <ReportError />
      </article>

      <aside className="book__side" aria-label={mainTitle(work, locale)}>
        {places.map(({ series, index }) => (
          <Nearby key={series.slug} catalogue={catalogue} world={world} series={series} index={index} />
        ))}
        <dl className="side-card book__facts">
          {work.year_first_published === null ? null : (
            <div>
              <dt className="label">{t('book.year')}</dt>
              <dd>{work.year_first_published}</dd>
            </div>
          )}
          <div>
            <dt className="label">{t('book.publication_order')}</dt>
            <dd>{fill(t('book.position'), { position: inPublication, count: catalogue.works.length })}</dd>
          </div>
        </dl>
        <ShelfPanel world={world} work={work.slug} />
      </aside>
    </div>
  )
}

/** Why the book stands where it does on one path, when the path says. */
function Why({ series, index }: { series: CatalogueSeries; index: number }) {
  const { t, locale } = useI18n()
  const why = note(series.books[index]?.note_bg ?? null, locale)
  if (why === null) return null
  const position = index + 1
  const word = ordinal(locale, position)
  return (
    <section className="why">
      <h2 className="label">
        {word === null ? fill(t('book.why.numbered'), { position }) : fill(t('book.why'), { ordinal: word })}
      </h2>
      <p>{why}</p>
    </section>
  )
}

/**
 * Three stops of the path around this book, this one marked. At either end
 * of the path the window slides rather than shrinks: the first book shows
 * itself and the two after it, the last the two before it.
 */
function Nearby({
  catalogue,
  world,
  series,
  index,
}: {
  catalogue: Loaded
  world: string
  series: CatalogueSeries
  index: number
}) {
  const { t, locale } = useI18n()
  const stopsLabel = useStops()
  const total = series.books.length
  const start = Math.max(0, Math.min(index - 1, total - 3))
  const name = seriesName(series, locale)

  const stops: PathStop[] = series.books.slice(start, start + 3).flatMap((book, offset) => {
    const work = workBySlug(catalogue, book.work)
    if (work === undefined) return []
    return [
      {
        position: start + offset + 1,
        title: mainTitle(work, locale),
        href: worldPath(world, 'books', work.slug),
        year: null,
      },
    ]
  })

  return (
    <section className="side-card side-card--path">
      <h2 className="label">{t('book.path.nearby')}</h2>
      <Path stops={stops} label={fill(t('book.path.label'), { series: name })} current={index + 1} compact />
      <p className="side-card__foot">
        <Link to={worldPath(world, 'series', series.slug)}>
          {fill(t('book.path.whole'), { stops: stopsLabel(total) })} ›
        </Link>
      </p>
    </section>
  )
}
